import React, { createContext, useState, useEffect } from "react"

const SHOPIFY_DOMAIN = "adam-monster.myshopify.com"
const SHOPIFY_ACCESS_TOKEN = "a75a894d6011c41902296fab46f97745"
const API_VERSION = "2025-10"
const STOREFRONT_URL = `https://${SHOPIFY_DOMAIN}/api/${API_VERSION}/graphql.json`

function logError(message, error) {
  // eslint-disable-next-line no-console
  console.error(`[StoreContext] ${message}`, error)
  if (typeof window !== "undefined" && window.Rollbar) {
    window.Rollbar.error(message, error)
  }
}

async function shopifyFetch(query, variables = {}) {
  const response = await fetch(STOREFRONT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": SHOPIFY_ACCESS_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  })
  if (!response.ok) {
    throw new Error(
      `Shopify API request failed: ${response.status} ${response.statusText}`
    )
  }
  const json = await response.json()
  if (json.errors) {
    throw new Error(json.errors.map((e) => e.message).join(", "))
  }
  return json.data
}

const CART_FIELDS = `
  fragment CartFields on Cart {
    id
    checkoutUrl
    lines(first: 250) {
      edges {
        node {
          id
          quantity
          merchandise {
            ... on ProductVariant {
              id
              title
              price {
                amount
              }
              image {
                url
              }
              product {
                title
              }
            }
          }
        }
      }
    }
    cost {
      subtotalAmount {
        amount
      }
    }
  }
`

const CART_CREATE_MUTATION = `
  ${CART_FIELDS}
  mutation cartCreate {
    cartCreate {
      cart { ...CartFields }
      userErrors { field message }
    }
  }
`

const CART_FETCH_QUERY = `
  ${CART_FIELDS}
  query cartFetch($cartId: ID!) {
    cart(id: $cartId) { ...CartFields }
  }
`

const CART_LINES_ADD_MUTATION = `
  ${CART_FIELDS}
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart { ...CartFields }
      userErrors { field message }
    }
  }
`

const CART_LINES_REMOVE_MUTATION = `
  ${CART_FIELDS}
  mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart { ...CartFields }
      userErrors { field message }
    }
  }
`

const CART_LINES_UPDATE_MUTATION = `
  ${CART_FIELDS}
  mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart { ...CartFields }
      userErrors { field message }
    }
  }
`

// Normalize Cart API response to match the shape expected by existing components
function normalizeCart(cart) {
  if (!cart) return { lineItems: [] }

  return {
    id: cart.id,
    lineItems: cart.lines.edges.map(({ node }) => ({
      id: node.id,
      title: node.merchandise.product.title,
      variant: {
        title: node.merchandise.title,
        price: node.merchandise.price.amount,
        image: { src: node.merchandise.image?.url || "" },
      },
      quantity: node.quantity,
    })),
    subtotalPrice: cart.cost.subtotalAmount.amount,
    webUrl: cart.checkoutUrl,
  }
}

// Ensure variant ID is in GID format for the Storefront API
function toVariantGid(variantId) {
  if (typeof variantId === "string" && variantId.startsWith("gid://")) {
    return variantId
  }
  return `gid://shopify/ProductVariant/${variantId}`
}

export const StoreContext = createContext({
  isUpdating: false,
  checkoutData: { lineItems: [] },
  products: [],
  addVariantToCart: () => {},
  removeLineItem: () => {},
  updateLineItem: () => {},
})

export const StoreProvider = ({ children }) => {
  const [isUpdating, setUpdating] = useState(false)
  const [checkoutData, setCheckoutData] = useState({ lineItems: [] })
  const [cartId, setCartId] = useState(null)

  const createNewCart = async () => {
    const data = await shopifyFetch(CART_CREATE_MUTATION)
    const cart = data.cartCreate.cart
    if (typeof window !== "undefined") {
      localStorage.setItem("shopify_cart_id", cart.id)
    }
    setCartId(cart.id)
    setCheckoutData(normalizeCart(cart))
  }

  const initCart = async () => {
    const isBrowser = typeof window !== "undefined"
    const existingCartId = isBrowser
      ? localStorage.getItem("shopify_cart_id")
      : null

    // Clear legacy checkout ID from old shopify-buy SDK
    if (isBrowser) {
      localStorage.removeItem("shopify_checkout_id")
    }

    try {
      if (existingCartId && existingCartId !== "null") {
        try {
          const data = await shopifyFetch(CART_FETCH_QUERY, {
            cartId: existingCartId,
          })
          if (data.cart) {
            setCartId(data.cart.id)
            setCheckoutData(normalizeCart(data.cart))
            return
          }
        } catch (e) {
          logError("Existing cart not found, creating new cart", e)
        }
      }

      await createNewCart()
    } catch (error) {
      logError("Failed to initialize cart", error)
    }
  }

  const handleAddVariantToCart = async (variantId, quantity) => {
    if (variantId === "" || !quantity) {
      logError("Both a size and quantity are required.", { variantId, quantity })
      return
    }

    setUpdating(true)

    try {
      const data = await shopifyFetch(CART_LINES_ADD_MUTATION, {
        cartId,
        lines: [
          {
            merchandiseId: toVariantGid(variantId),
            quantity: parseInt(quantity, 10),
          },
        ],
      })
      setCheckoutData(normalizeCart(data.cartLinesAdd.cart))
    } catch (error) {
      logError("Failed to add item to cart", error)
    } finally {
      setUpdating(false)
    }
  }

  const handleRemoveLineItem = async (lineItemId) => {
    setUpdating(true)

    try {
      const data = await shopifyFetch(CART_LINES_REMOVE_MUTATION, {
        cartId,
        lineIds: [lineItemId],
      })
      setCheckoutData(normalizeCart(data.cartLinesRemove.cart))
    } catch (error) {
      logError("Failed to remove item from cart", error)
    } finally {
      setUpdating(false)
    }
  }

  const handleUpdateLineItem = async (lineItemId, quantity) => {
    setUpdating(true)

    try {
      const data = await shopifyFetch(CART_LINES_UPDATE_MUTATION, {
        cartId,
        lines: [{ id: lineItemId, quantity: parseInt(quantity, 10) }],
      })
      setCheckoutData(normalizeCart(data.cartLinesUpdate.cart))
    } catch (error) {
      logError("Failed to update cart item", error)
    } finally {
      setUpdating(false)
    }
  }

  useEffect(() => {
    initCart()
  }, [])

  return (
    <StoreContext.Provider
      value={{
        isUpdating,
        checkoutData,
        addVariantToCart: handleAddVariantToCart,
        removeLineItem: handleRemoveLineItem,
        updateLineItem: handleUpdateLineItem,
      }}
    >
      {children}
    </StoreContext.Provider>
  )
}

export default StoreContext
